import { useEffect } from 'react'
import { ChevronDown, Palette } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useDirectionStore, DIRECTIONS, type Direction } from '@/store/directionStore'

// Label keys typed explicitly to satisfy i18next strict typing
const LABEL_KEY: Record<Direction, 'direction.current' | 'direction.command' | 'direction.saas' | 'direction.expressive'> = {
  current: 'direction.current',
  command: 'direction.command',
  saas: 'direction.saas',
  expressive: 'direction.expressive',
}

// Swatch dot per direction: primary token rendered literally so the menu
// previews each design without switching to it.
const DOT_COLOR: Record<Direction, string> = {
  current: 'hsl(41 91% 43.5%)',
  command: 'hsl(41 95% 52%)',
  saas: 'hsl(240 6% 10%)',
  expressive: 'hsl(38 96% 50%)',
}

export function DirectionSwitcher() {
  const { t } = useTranslation('common')
  const { direction, setDirection, cycleDirection } = useDirectionStore()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.shiftKey || (e.key !== 'D' && e.key !== 'd')) return
      const el = document.activeElement as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return
      e.preventDefault()
      cycleDirection()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [cycleDirection])

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-2 py-1 text-xs font-medium text-foreground hover:bg-muted hover:border-border transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title={`${t('direction.label')} — ${t('direction.hint')}`}
          aria-label={t('direction.label')}
        >
          <Palette className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: DOT_COLOR[direction] }} />
          <span className="hidden md:inline font-semibold tracking-wide">{t(LABEL_KEY[direction])}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 min-w-[180px] rounded-lg border border-border bg-popover p-1 shadow-elevation-2 animate-in fade-in-0 zoom-in-95"
          sideOffset={5}
          align="end"
        >
          {DIRECTIONS.map((opt) => (
            <DropdownMenu.Item
              key={opt.id}
              className={`relative flex cursor-pointer select-none items-center gap-2.5 rounded-md px-3 py-2 text-sm outline-none transition-colors hover:bg-muted ${
                direction === opt.id ? 'bg-primary/8 font-semibold text-primary' : 'text-foreground'
              }`}
              onSelect={() => setDirection(opt.id)}
            >
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: DOT_COLOR[opt.id] }} />
              <span className="flex-1">{t(LABEL_KEY[opt.id])}</span>
              {direction === opt.id && <span className="text-primary text-xs">&#10003;</span>}
            </DropdownMenu.Item>
          ))}
          <div className="px-3 pb-1.5 pt-1 text-[10px] text-muted-foreground">{t('direction.hint')}</div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
